/**
 * Date and time formatting for the CRM-backed portal pages.
 *
 * Deliberately not `<FormattedDate>`: that component parses with
 * `parseAbsolute`, which needs a full ISO instant with an offset. CRM rows
 * carry `2026-09-04` and `2026-09-04 10:00:00`, and both make it throw — and it
 * swallows the error and renders nothing, so the date silently disappears
 * rather than looking wrong. These take the CRM's shapes as they come.
 */

/**
 * Money, written the way people here write it.
 *
 * `Intl.NumberFormat(undefined, …)` follows the BROWSER locale, which renders
 * USD as "US$9.99" on most of them and switches to Arabic-Indic digits on an
 * Arabic one — so the same price looked like three different prices depending
 * on who was looking. Pinned to en-US: "$9.99", Western digits, everywhere.
 *
 * Decimals only when they carry information: `$9.99`, but `$1,200` rather than
 * `$1,200.00`. Rounding to whole numbers unconditionally (which the dashboard
 * tiles used to do) turned $9.99 into $10, which is a lie about money.
 */
export function money(amount?: number | string | null, currency?: string | null): string {
  const n = Number(amount ?? 0) || 0;
  // The CRM leaves `currency` empty on invoices it did not price itself.
  const code = (currency || 'USD').toUpperCase();
  const whole = Math.abs(n % 1) < 0.005;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(n);
  } catch {
    // An unknown code must not blank the number out.
    return `${code} ${whole ? n.toFixed(0) : n.toFixed(2)}`;
  }
}

/**
 * Month names, written out per locale for the same reason the calendar does:
 * the browser's Arabic data is "آب" on one machine and "أغسطس" on the next.
 */
const MONTHS_SHORT: Record<string, string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
       'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
  tr: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
};

/** The active UI language, read without a hook so plain functions can use it. */
function uiLocale(): string {
  return document.documentElement.lang || 'en';
}

/** `2026-09-04` or `2026-09-04 10:00:00` → "4 Sep 2026" / "4 سبتمبر 2026". */
export function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  if (isNaN(+d)) return '—';

  const months = MONTHS_SHORT[uiLocale()] ?? MONTHS_SHORT.en;

  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/** Same, plus the clock. */
export function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  if (isNaN(+d)) return '—';

  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');

  return `${fmtDate(value)} ${hh}:${mm}`;
}

/**
 * `15:30:00` → "3:30 PM", or "٣:٣٠ م" — sorry, "3:30 م".
 *
 * Twenty-four hour time is what the CRM stores and what nobody says out loud.
 * "15:30" also gave no clue whether a 9:00 call was morning or evening, which
 * is the one thing a student needs from an appointment.
 *
 * The digits stay Latin in Arabic on purpose: this application writes numbers
 * that way everywhere else, and a time is not the place to start disagreeing.
 */
const MERIDIEM: Record<string, [string, string]> = {
  en: ['AM', 'PM'],
  ar: ['ص', 'م'],
  tr: ['ÖÖ', 'ÖS'],
};

export function fmtTime(value?: string | null): string {
  if (!value) return '';

  const [rawHour, rawMinute] = String(value).split(':');
  const hour24 = Number(rawHour);
  if (!Number.isFinite(hour24)) return String(value).slice(0, 5);

  const [am, pm] = MERIDIEM[uiLocale()] ?? MERIDIEM.en;
  // 0 → 12am, 12 → 12pm; the modulo alone turns both into "0".
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return `${hour12}:${(rawMinute ?? '00').slice(0, 2)} ${hour24 < 12 ? am : pm}`;
}

/** The raw 24-hour clock, for the rare place that wants it. */
export function fmtTime24(value?: string | null): string {
  return value ? String(value).slice(0, 5) : '';
}

/** The weekday and day-of-month, for a calendar-style badge. */
export function fmtDayBadge(value?: string | null): {day: string; month: string; weekday: string} {
  const d = value ? new Date(String(value).replace(' ', 'T')) : null;
  if (!d || isNaN(+d)) return {day: '—', month: '', weekday: ''};

  const locale = uiLocale();
  const months = MONTHS_SHORT[locale] ?? MONTHS_SHORT.en;
  const weekdays = WEEKDAYS_SHORT[locale] ?? WEEKDAYS_SHORT.en;

  return {
    day: String(d.getDate()),
    month: months[d.getMonth()],
    weekday: weekdays[d.getDay()],
  };
}


/** Sunday-first, matching `Date.getDay()`. */
const WEEKDAYS_SHORT: Record<string, string[]> = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
  tr: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
};

/**
 * A price with its currency symbol.
 *
 * The CRM leaves `currency_symbol` empty on most service rows, so the portal was
 * printing a bare "120" and leaving the student to guess the currency — dollars,
 * pounds or lira are all plausible to the people reading this (owner, 2026-10-04).
 * Masertak bills in USD, so that is the fallback; a symbol the CRM does supply
 * still wins, because a service priced in anything else must say so.
 *
 * The symbol is kept next to the digits inside one bdi by the callers, so it does
 * not get flipped to the far side of the number in Arabic.
 */
export function fmtMoney(value: number | string | null | undefined, symbol?: string | null): string {
  const n = Number(value ?? 0);
  if (!isFinite(n)) return '';

  return `${symbol || '$'}${n.toLocaleString()}`;
}
